import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { chuXuongDong } from "lib/kieuBangDuLieu";
import { danhMucApi } from "api/danhMuc";
import useTour from "hooks/useTour";
import buocDanhMuc from "tours/danhMuc";

function DanhSachDon({ nhan, tieuDe, moTa, danhSach, taoMoi, sua, xoa, taiLai }) {
  const [dangThem, setDangThem] = useState(false);
  const [giaTriMoi, setGiaTriMoi] = useState("");
  const [dangSuaId, setDangSuaId] = useState(null);
  const [giaTriSua, setGiaTriSua] = useState("");
  const [loi, setLoi] = useState("");

  async function themMoi(e) {
    e.preventDefault();
    if (!giaTriMoi.trim()) return;
    try {
      await taoMoi(giaTriMoi.trim());
      setGiaTriMoi("");
      setDangThem(false);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    }
  }

  function batDauSua(item) {
    setDangSuaId(item.id);
    setGiaTriSua(item.tenLoai ?? item.tenHinhThuc ?? "");
  }

  async function luuSua(id) {
    try {
      await sua(id, giaTriSua.trim());
      setDangSuaId(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    }
  }

  async function xoaItem(id) {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Xoá mục này khỏi danh mục?")) return;
    try {
      await xoa(id);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    }
  }

  return (
    <Card id="tour-quan-ly-danh-muc-danh-sach" sx={{ mb: 4 }}>
      <MDBox
        px={2.5}
        py={2}
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
      >
        <MDBox>
          <NhanDau>{nhan}</NhanDau>
          <MDTypography variant="h6">{tieuDe}</MDTypography>
          <MDTypography variant="button" fontWeight="regular" color="text">
            {moTa}
          </MDTypography>
        </MDBox>
        {!dangThem && (
          <MDButton
            id="tour-quan-ly-danh-muc-nut-them"
            variant="outlined"
            color="info"
            size="small"
            onClick={() => setDangThem(true)}
          >
            <Icon sx={{ mr: 0.5 }}>add</Icon>
            Thêm
          </MDButton>
        )}
      </MDBox>

      {loi && (
        <MDBox px={2.5} pb={1}>
          <MDAlert color="error">{loi}</MDAlert>
        </MDBox>
      )}

      {danhSach.length === 0 ? (
        <MDBox px={2.5} pb={4} pt={1} textAlign="center">
          <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
            list_alt
          </Icon>
          <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
            Chưa có mục nào.
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
            Bấm “Thêm” để tạo mục đầu tiên.
          </MDTypography>
        </MDBox>
      ) : (
        <List sx={{ py: 0 }}>
          {danhSach.map((item, i) => {
            const ten = item.tenLoai ?? item.tenHinhThuc;
            return (
              <MDBox key={item.id}>
                <ListItem
                  sx={({ palette }) => ({
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    py: 1.25,
                    px: 2.5,
                    transition: "background-color 160ms ease",
                    "&:hover": { backgroundColor: palette.grey[100] },
                  })}
                >
                  {dangSuaId === item.id ? (
                    <MDBox display="flex" gap={1} width="100%" alignItems="center">
                      <MDInput
                        value={giaTriSua}
                        onChange={(e) => setGiaTriSua(e.target.value)}
                        autoFocus
                        fullWidth
                      />
                      <MDButton size="small" color="info" onClick={() => luuSua(item.id)}>
                        Lưu
                      </MDButton>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => setDangSuaId(null)}
                      >
                        Huỷ
                      </MDButton>
                    </MDBox>
                  ) : (
                    <>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        sx={{ ...chuXuongDong, flex: 1, minWidth: 0 }}
                      >
                        {ten}
                      </MDTypography>
                      <MDBox display="flex" gap={1} flexShrink={0}>
                        <MDButton
                          size="small"
                          variant="text"
                          color="info"
                          onClick={() => batDauSua(item)}
                        >
                          Sửa
                        </MDButton>
                        <MDButton
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => xoaItem(item.id)}
                        >
                          Xoá
                        </MDButton>
                      </MDBox>
                    </>
                  )}
                </ListItem>
                {i < danhSach.length - 1 && <Divider sx={{ my: 0 }} />}
              </MDBox>
            );
          })}
        </List>
      )}

      {dangThem && (
        <MDBox px={2.5} py={2} component="form" onSubmit={themMoi} display="flex" gap={1}>
          <MDInput
            value={giaTriMoi}
            onChange={(e) => setGiaTriMoi(e.target.value)}
            placeholder="Tên mới..."
            autoFocus
            fullWidth
          />
          <MDButton type="submit" variant="gradient" color="info">
            Lưu
          </MDButton>
          <MDButton variant="outlined" color="secondary" onClick={() => setDangThem(false)}>
            Huỷ
          </MDButton>
        </MDBox>
      )}
    </Card>
  );
}

DanhSachDon.propTypes = {
  nhan: PropTypes.string.isRequired,
  tieuDe: PropTypes.string.isRequired,
  moTa: PropTypes.string.isRequired,
  danhSach: PropTypes.arrayOf(PropTypes.object).isRequired,
  taoMoi: PropTypes.func.isRequired,
  sua: PropTypes.func.isRequired,
  xoa: PropTypes.func.isRequired,
  taiLai: PropTypes.func.isRequired,
};

function DanhMuc() {
  const [hinhThucTtList, setHinhThucTtList] = useState([]);
  const [dangTai, setDangTai] = useState(true);

  async function taiLai() {
    // Realtime onSnapshot cập nhật danh sách.
  }

  useEffect(() => {
    setDangTai(true);
    const huy = danhMucApi.langNgheHinhThucThanhToan(
      (ds) => {
        setHinhThucTtList(ds);
        setDangTai(false);
      },
      () => setDangTai(false)
    );
    return huy;
  }, []);

  useTour("quan-ly-danh-muc", dangTai ? [] : buocDanhMuc());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Danh mục"
          moTa="Các danh sách chọn dùng lại trong form thu mua."
        />

        {dangTai ? (
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        ) : (
          <DanhSachDon
            nhan="Danh mục"
            tieuDe="Hình thức thanh toán"
            moTa="Dùng trong form thu mua."
            danhSach={hinhThucTtList}
            taoMoi={danhMucApi.taoHinhThucTt}
            sua={danhMucApi.suaHinhThucTt}
            xoa={danhMucApi.xoaHinhThucTt}
            taiLai={taiLai}
          />
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DanhMuc;
